/**
 * Defensive parser for the v1.6 SetChargingProfile inbound payload.
 *
 * The wire-level schema validates SHAPE; this parser layers the
 * domain-level invariants on top (branded IDs in range, enums match
 * `ProfilePurpose` / `ProfileKind` / `RecurrencyKind`, optional fields
 * either absent or well-formed). Returns `null` for any rejection —
 * the store-side handler then replies `Rejected` per OCPP 1.6J §5.16
 * (SetChargingProfileStatus enum: { Accepted, Rejected, NotSupported }).
 *
 * Session 12 / CR 2026-05-27 §4.1 — lifted out of `useStationStore.ts`.
 * Same logic, just classed as wire parsing where it belongs.
 */
import { ChargingProfile, type ChargingProfileSnapshot } from '@/domain/ChargingProfile';
import {
  ChargingProfileId,
  ProfileKind,
  ProfilePurpose,
  RecurrencyKind,
  TransactionId,
  type ChargingRateUnit,
  type ChargingSchedule,
  type ConnectorId,
} from '@/domain/types';
import { parseDateTime } from '@/lib/parseDateTime';

export function parseSetChargingProfilePayload(
  connectorIdRaw: 0 | ConnectorId,
  cp: Record<string, unknown>,
): ChargingProfileSnapshot | null {
  const idRaw = cp['chargingProfileId'];
  if (typeof idRaw !== 'number' || !Number.isInteger(idRaw)) return null;
  let id: ChargingProfileId;
  try {
    id = ChargingProfileId.of(idRaw);
  } catch {
    return null;
  }
  const stackLevel = cp['stackLevel'];
  if (typeof stackLevel !== 'number' || !Number.isInteger(stackLevel) || stackLevel < 0) {
    return null;
  }
  const purposeRaw = cp['chargingProfilePurpose'];
  if (typeof purposeRaw !== 'string') return null;
  const purpose = (Object.values(ProfilePurpose) as readonly string[]).includes(purposeRaw)
    ? (purposeRaw as ProfilePurpose)
    : null;
  if (purpose === null) return null;
  const kindRaw = cp['chargingProfileKind'];
  if (typeof kindRaw !== 'string') return null;
  const kind = (Object.values(ProfileKind) as readonly string[]).includes(kindRaw)
    ? (kindRaw as ProfileKind)
    : null;
  if (kind === null) return null;
  const recurrencyRaw = cp['recurrencyKind'];
  let recurrencyKind: RecurrencyKind | undefined;
  if (recurrencyRaw !== undefined) {
    if (typeof recurrencyRaw !== 'string') return null;
    if (!(Object.values(RecurrencyKind) as readonly string[]).includes(recurrencyRaw)) return null;
    recurrencyKind = recurrencyRaw as RecurrencyKind;
  }
  const validFrom = parseDateTime(cp['validFrom']);
  if (validFrom === 'invalid') return null;
  const validTo = parseDateTime(cp['validTo']);
  if (validTo === 'invalid') return null;
  let transactionId: TransactionId | undefined;
  if (cp['transactionId'] !== undefined) {
    if (typeof cp['transactionId'] !== 'number' || !Number.isInteger(cp['transactionId'])) {
      return null;
    }
    try {
      transactionId = TransactionId.of(cp['transactionId']);
    } catch {
      return null;
    }
  }
  const sched = cp['chargingSchedule'];
  if (sched === null || typeof sched !== 'object') return null;
  const schedule = parseChargingSchedule(sched as Record<string, unknown>);
  if (schedule === null) return null;

  const snap: ChargingProfileSnapshot = {
    id,
    stackLevel,
    purpose,
    kind,
    ...(recurrencyKind !== undefined ? { recurrencyKind } : {}),
    ...(validFrom !== undefined ? { validFrom } : {}),
    ...(validTo !== undefined ? { validTo } : {}),
    ...(transactionId !== undefined ? { transactionId } : {}),
    connectorId: connectorIdRaw === 0 ? 0 : Number(connectorIdRaw),
    schedule,
  };
  return snap;
}

export function parseChargingSchedule(
  s: Record<string, unknown>,
): ChargingSchedule | null {
  const rateUnitRaw = s['chargingRateUnit'];
  if (rateUnitRaw !== 'A' && rateUnitRaw !== 'W') return null;
  // After the literal-narrow guard above, `rateUnitRaw` is already
  // `'A' | 'W'` — which equals ChargingRateUnit, so no cast needed.
  const chargingRateUnit: ChargingRateUnit = rateUnitRaw;
  const periodsRaw = s['chargingSchedulePeriod'];
  if (!Array.isArray(periodsRaw) || periodsRaw.length === 0) return null;
  const periods: ChargingSchedule['chargingSchedulePeriod'][number][] = [];
  for (const p of periodsRaw) {
    if (p === null || typeof p !== 'object') return null;
    const row = p as Record<string, unknown>;
    if (typeof row['startPeriod'] !== 'number' || typeof row['limit'] !== 'number') {
      return null;
    }
    periods.push({
      startPeriod: row['startPeriod'],
      limit: row['limit'],
      ...(typeof row['numberPhases'] === 'number' ? { numberPhases: row['numberPhases'] } : {}),
    });
  }
  const out: ChargingSchedule = {
    chargingRateUnit,
    chargingSchedulePeriod: periods,
    ...(typeof s['duration'] === 'number' ? { duration: s['duration'] } : {}),
    ...(typeof s['startSchedule'] === 'string'
      ? { startSchedule: Date.parse(s['startSchedule']) }
      : {}),
    ...(typeof s['minChargingRate'] === 'number' ? { minChargingRate: s['minChargingRate'] } : {}),
  };
  return out;
}

/**
 * Re-export the domain class so the store-side handler can construct
 * a `ChargingProfile` to enforce invariants after parsing. Keeping it
 * here avoids an extra `@/domain/ChargingProfile` import in the
 * handler file.
 */
export { ChargingProfile };
